import { useStoryblokApi } from '@storyblok/react';
import type { ISbStoryData } from '@storyblok/react';
import { useQuery } from '@tanstack/react-query';

export type StoryState =
  | { status: 'loading' }
  | { status: 'found'; story: ISbStoryData }
  | { status: 'not-found' };

export const getStoryVersion = (search: string, isDevelopment: boolean): 'draft' | 'published' =>
  isDevelopment || new URLSearchParams(search).has('_storyblok') ? 'draft' : 'published';

/**
 * Fetches a Storyblok story by slug with explicit loading / not-found states.
 *
 * Unlike `useStoryblok`, this hook does not initialise as `{}` — it starts as
 * `{ status: 'loading' }` and transitions to `'found'` or `'not-found'` once
 * the API call resolves, giving callers reliable control over each state.
 */
export const useStory = (slug: string): StoryState => {
  const api = useStoryblokApi();
  const version = getStoryVersion(window.location.search, import.meta.env.DEV);
  const query = useQuery({
    queryKey: ['storyblok', slug, version],
    queryFn: async () => {
      const { data } = await api.get(`cdn/stories/${slug}`, { version });
      return data.story as ISbStoryData | undefined;
    },
    retry: false,
  });

  if (query.isPending) {
    return { status: 'loading' };
  }
  return query.data ? { status: 'found', story: query.data } : { status: 'not-found' };
};
